import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiArrowLeft, FiEdit2, FiPrinter, FiCalendar } from "react-icons/fi";
import timetableService from "../../services/timetableService";
import { useApp } from "../../context/AppContext";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const TYPE_COLORS = {
    Lecture: { bg: "#e8f1f8", color: "#002747", border: "#93c5fd" },
    Practical: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    Tutorial: { bg: "#fef9c3", color: "#92400e", border: "#fde047" },
    Break: { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" },
    Free: { bg: "#ffffff", color: "#d1d5db", border: "#e5e7eb" },
};

const COURSE_COLORS = {
    "D. PHARM": "#003b67",
    "B. PHARM": "#059669",
    "M. PHARM": "#7c3aed",
};

const normalizeTimetable = (payload) => payload?.data || payload || null;

export default function TimetableView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isLoggedIn } = useApp();

    const [timetable, setTimetable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

    useEffect(() => {
        timetableService
            .getById(id)
            .then((res) => setTimetable(normalizeTimetable(res)))
            .catch((err) => {
                toast.error(err.message);
                navigate("/timetables");
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading timetable...
            </div>
        );
    if (!timetable) return null;

    const {
        course,
        courseName,
        yearNumber,
        yearLabel,
        semesterNumber,
        semesterLabel,
        academicYear,
        periodSlots = [],
        schedule = [],
        notes,
        effectiveFrom,
    } = timetable;
    const courseTitle = course?.shortName || courseName || "Course";
    const displayYear = yearLabel || (yearNumber ? `${yearNumber}${yearNumber === 1 ? "st" : yearNumber === 2 ? "nd" : yearNumber === 3 ? "rd" : "th"} Year` : "");
    const displaySemester = semesterLabel || (semesterNumber ? `Semester ${semesterNumber}` : "");
    const courseColor = COURSE_COLORS[courseTitle] || "#6b7280";

    const getPeriodSubjectName = (period) => {
        if (!period) return "";
        if (period.subjectName) return period.subjectName;
        if (typeof period.subject === "object" && period.subject?.name) return period.subject.name;
        return typeof period.subject === "string" ? period.subject : "";
    };

    // Build lookup: day → periods[]
    const scheduleMap = Object.fromEntries(
        schedule.map((d) => [d.day, d.periods || []]),
    );

    // Stats
    const allPeriods = schedule.flatMap((d) => d.periods);
    const filled = allPeriods.filter(
        (p) => (p.subject || p.subjectName) && p.type !== "Break" && p.type !== "Free",
    );
    const subjects = [...new Set(filled.map((p) => getPeriodSubjectName(p)).filter(Boolean))];
    const teachers = [...new Set(filled.map((p) => p.teacher).filter(Boolean))];

    return (
        <div>
            {/* ── Header ── */}
            <div
                className="page-header no-print"
                style={{ flexWrap: "wrap", gap: "0.5rem" }}
            >
                <div>
                    <h1 className="page-title">
                        {courseTitle} — {displayYear}{displaySemester ? ` · ${displaySemester}` : ""}
                    </h1>
                    <p className="page-subtitle">
                        {academicYear?.label}
                        {effectiveFrom ? ` · Effective from ${effectiveFrom}` : ""}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/timetables")}
                    >
                        <FiArrowLeft size={14} /> Back
                    </button>
                    {/* View toggle */}
                    <div
                        style={{
                            display: "flex",
                            border: "1px solid var(--border)",
                            borderRadius: "var(--radius)",
                            overflow: "hidden",
                        }}
                    >
                        {["grid", "list"].map((mode) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setViewMode(mode)}
                                style={{
                                    padding: "0.4rem 0.85rem",
                                    fontSize: 12.5,
                                    fontWeight: 500,
                                    border: "none",
                                    cursor: "pointer",
                                    background:
                                        viewMode === mode ? "var(--primary)" : "transparent",
                                    color: viewMode === mode ? "#fff" : "var(--text-muted)",
                                    textTransform: "capitalize",
                                }}
                            >
                                {mode}
                            </button>
                        ))}
                    </div>
                    {isLoggedIn && (
                        <button
                            className="btn btn-outline"
                            onClick={() => navigate(`/timetables/${id}/edit`)}
                        >
                            <FiEdit2 size={14} /> Edit
                        </button>
                    )}
                    <button className="btn btn-outline" onClick={() => window.print()}>
                        <FiPrinter size={14} /> Print
                    </button>
                </div>
            </div>

            {/* ── Summary chips ── */}
            <div
                className="no-print"
                style={{
                    display: "flex",
                    gap: "0.6rem",
                    marginBottom: "1rem",
                    flexWrap: "wrap",
                }}
            >
                {[
                    { label: "Subjects", value: subjects.length },
                    { label: "Teachers", value: teachers.length },
                    { label: "Periods/week", value: filled.length },
                    { label: "Period slots", value: periodSlots.length },
                ].map(({ label, value }) => (
                    <div
                        key={label}
                        style={{
                            background: "#fff",
                            border: "1px solid var(--border)",
                            borderRadius: 8,
                            padding: "0.5rem 1rem",
                            fontSize: 13,
                        }}
                    >
                        <span style={{ fontWeight: 700, color: courseColor }}>{value}</span>
                        <span style={{ color: "var(--text-muted)", marginLeft: 5 }}>
                            {label}
                        </span>
                    </div>
                ))}
            </div>

            {/* ══════════════════════════════════════════════════════════════
          GRID VIEW — day columns × period rows
      ══════════════════════════════════════════════════════════════ */}
            {viewMode === "grid" && (
                <div className="card print-area" style={{ overflowX: "auto" }}>
                    {/* Print header */}
                    <div className="only-print" style={{ padding: "0.5rem 1rem" }}>
                        <CollegePrintHeader
                            layout="banner"
                            logoSize={54}
                            title={`TIMETABLE — ${courseTitle} ${displayYear}`}
                            subtitle={`${displaySemester ? `${displaySemester} · ` : ""}Academic Year: ${academicYear?.label || "—"}${effectiveFrom ? ` · Effective: ${effectiveFrom}` : ""}`}
                        />
                    </div>

                    <div style={{ minWidth: 700 }}>
                        {/* Column headers: Time | Mon | Tue … Sat */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: `110px repeat(${DAYS.length}, 1fr)`,
                                background: "var(--bg)",
                                borderBottom: "1px solid var(--border)",
                            }}
                        >
                            <div
                                style={{
                                    padding: "0.6rem 0.75rem",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    color: "var(--text-muted)",
                                }}
                            >
                                Time
                            </div>
                            {DAYS.map((day) => (
                                <div
                                    key={day}
                                    style={{
                                        padding: "0.6rem 0.5rem",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "var(--text)",
                                        textAlign: "center",
                                        borderLeft: "1px solid var(--border)",
                                    }}
                                >
                                    {day.slice(0, 3).toUpperCase()}
                                </div>
                            ))}
                        </div>

                        {/* Period rows */}
                        {periodSlots.map((slot, slotIdx) => (
                            <div
                                key={slot.periodNo}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: `110px repeat(${DAYS.length}, 1fr)`,
                                    borderBottom: "1px solid var(--border)",
                                    minHeight: 68,
                                }}
                            >
                                {/* Time cell */}
                                <div
                                    style={{
                                        padding: "0.5rem 0.75rem",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        background: "var(--bg)",
                                        borderRight: "1px solid var(--border)",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            color: "var(--text)",
                                        }}
                                    >
                                        {slot.startTime} – {slot.endTime}
                                    </div>
                                    {slot.label && (
                                        <div
                                            style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 1 }}
                                        >
                                            {slot.label}
                                        </div>
                                    )}
                                </div>

                                {/* Day cells */}
                                {DAYS.map((day) => {
                                    const dayPeriods = scheduleMap[day] || [];
                                    const period = dayPeriods[slotIdx];
                                    const subjectDisplayName = getPeriodSubjectName(period);
                                    const isEmpty = !subjectDisplayName;
                                    const tc = TYPE_COLORS[period?.type] || TYPE_COLORS.Free;

                                    return (
                                        <div
                                            key={day}
                                            style={{
                                                padding: "0.4rem 0.5rem",
                                                borderLeft: "1px solid var(--border)",
                                                background: isEmpty ? "transparent" : tc.bg,
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {!isEmpty && (
                                                <>
                                                    <div
                                                        style={{
                                                            fontSize: 12.5,
                                                            fontWeight: 700,
                                                            color: tc.color,
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        {subjectDisplayName}
                                                    </div>
                                                    {period.teacher && (
                                                        <div
                                                            style={{
                                                                fontSize: 11,
                                                                color: tc.color,
                                                                opacity: 0.75,
                                                                marginTop: 1,
                                                             }}
                                                        >
                                                            {period.teacher}
                                                        </div>
                                                    )}
                                                    {period.room && (
                                                        <div
                                                            style={{
                                                                fontSize: 10.5,
                                                                color: tc.color,
                                                                opacity: 0.6,
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            📍 {period.room}
                                                        </div>
                                                    )}
                                                    <div style={{ marginTop: 3 }}>
                                                        <span
                                                            style={{
                                                                fontSize: 9.5,
                                                                background: tc.color + "22",
                                                                color: tc.color,
                                                                padding: "1px 5px",
                                                                borderRadius: 99,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {period.type}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Notes & subjects legend */}
                    {(notes || subjects.length > 0) && (
                        <div
                            style={{
                                padding: "0.85rem 1rem",
                                borderTop: "1px solid var(--border)",
                                display: "flex",
                                gap: "1.5rem",
                                flexWrap: "wrap",
                            }}
                        >
                            {notes && (
                                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                                    <strong>Notes:</strong> {notes}
                                </div>
                            )}
                            {subjects.length > 0 && (
                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                    <strong>Subjects:</strong> {subjects.join(" · ")}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
          LIST VIEW — day by day, periods as rows
      ══════════════════════════════════════════════════════════════ */}
            {viewMode === "list" && (
                <div style={{ display: "grid", gap: "1rem" }}>
                    {DAYS.map((day) => {
                        const dayPeriods = scheduleMap[day] || [];
                        const hasFilled = dayPeriods.some((p) => p.subject);
                        return (
                            <div key={day} className="card">
                                <div
                                    className="card-header"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.4rem",
                                    }}
                                >
                                    <FiCalendar size={13} /> {day}
                                    {hasFilled && (
                                        <span
                                            style={{
                                                marginLeft: 6,
                                                fontSize: 11,
                                                background: courseColor,
                                                color: "#fff",
                                                padding: "1px 7px",
                                                borderRadius: 99,
                                            }}
                                        >
                                            {dayPeriods.filter((p) => p.subject).length} period
                                            {dayPeriods.filter((p) => p.subject).length !== 1
                                                ? "s"
                                                : ""}
                                        </span>
                                    )}
                                </div>
                                {!hasFilled ? (
                                    <div
                                        style={{
                                            padding: "0.85rem 1.25rem",
                                            fontSize: 13,
                                            color: "var(--text-muted)",
                                        }}
                                    >
                                        No classes scheduled.
                                    </div>
                                ) : (
                                    <div>
                                        {dayPeriods.map((period, pi) => {
                                            const slot = periodSlots[pi] || {};
                                            const subjectDisplayName = getPeriodSubjectName(period);
                                            const isEmpty = !subjectDisplayName;
                                            const tc = TYPE_COLORS[period.type] || TYPE_COLORS.Free;
                                            if (isEmpty) return null;
                                            return (
                                                <div
                                                    key={pi}
                                                    style={{
                                                        display: "flex",
                                                        gap: "1rem",
                                                        alignItems: "center",
                                                        padding: "0.65rem 1.25rem",
                                                        borderBottom: "1px solid var(--border)",
                                                        background: tc.bg,
                                                    }}
                                                >
                                                    {/* Time */}
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: "var(--text-muted)",
                                                            minWidth: 110,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {slot.startTime} – {slot.endTime}
                                                    </div>
                                                    {/* Type badge */}
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            color: tc.color,
                                                            background: "#fff",
                                                            padding: "2px 8px",
                                                            borderRadius: 99,
                                                            border: `1px solid ${tc.border}`,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {period.type}
                                                    </span>
                                                    {/* Subject */}
                                                    <div style={{ flex: 1 }}>
                                                        <div
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: 13.5,
                                                                color: tc.color,
                                                            }}
                                                        >
                                                            {subjectDisplayName}
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: "var(--text-muted)",
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            {period.teacher && <span>{period.teacher}</span>}
                                                            {period.teacher && period.room && (
                                                                <span style={{ margin: "0 6px" }}>·</span>
                                                            )}
                                                            {period.room && <span>📍 {period.room}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
